<template>
  <div id="reports" class="fill-height align-start">
    <v-row class="g-container" no-gutters>
      <v-col>
        <v-row class="ma-0">
          <h2>Reports</h2>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3 class="g-heading-xs mb-n2">Trading Reports</h3>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="d-flex"
            v-for="card in tradingReports.filter((c) => !c.hidden)"
            :key="card.title"
          >
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
              :class="{ 'p-card-disabled': card.disabled }"
              :ripple="!card.disabled"
              @click="openReport(card)"
            >
              <v-row align="center">
                <v-icon class="p-icon">{{ card.icon }}</v-icon>
                <span class="g-heading-xs g-bold">{{ card.title }}</span>
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <div>
                <ul v-for="text in card.text" :key="text">
                  <li>{{ text }}</li>
                </ul>
              </div>
              <div v-if="card.disabledReason" class="mt-3 p-disabled-text">
                {{ card.disabledReason }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3 class="g-heading-xs mb-n2">Sales Reports</h3>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="d-flex"
            v-for="card in salesReports.filter((c) => !c.hidden)"
            :key="card.title"
          >
            <v-card
              :to="{ name: card.routeName }"
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">{{ card.icon }}</v-icon>
                <span class="g-heading-xs g-bold">{{ card.title }}</span>
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <div>
                <ul v-for="text in card.text" :key="text">
                  <li>{{ text }}</li>
                </ul>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  data: () => ({}),

  computed: {
    ...mapGetters('profile', [
      'bankDetailsAvailable',
      'creditReturnEnabled',
      'processesOrdersOffline'
    ]),
    ...mapGetters('tenant', [
      'featureBankAccountsEnabled',
      'featurePaymentServicesPaymentEnabled',
      'featureCreditCardPaymentEnabled',
      'featureCreditReturnsEnabled',
      'featureForecastEnabled',
      'isILR'
    ]),

    cardServicesAvailable() {
      return (
        this.featureCreditCardPaymentEnabled &&
        !this.processesOrdersOffline &&
        (!this.featureBankAccountsEnabled || this.bankDetailsAvailable)
      )
    },

    tradingReports() {
      return [
        {
          title: 'Purchase Order History',
          text: ['Delivered Purchase Orders', 'Export Purchase Orders'],
          icon: 'mdi-history',
          routeName: 'purchase-orders-history'
        },
        {
          val: 'order-queries-history',
          title: 'Order Queries History',
          text: ['Approved Order Queries', 'Rejected Order Queries'],
          icon: 'mdi-clock-alert-outline',
          routeName: 'order-queries-history'
        },
        {
          val: 'credit-returns-history',
          title: 'Credit Returns History',
          text: ['Approved Credit Returns', 'Rejected Credit Returns'],
          icon: 'mdi-credit-card-refund-outline',
          routeName: 'credit-returns-history',
          hidden: !this.creditReturnEnabled
        },
        {
          title: 'Payment Adjustments',
          text: [
            'Review Payment Adjustments',
            this.featurePaymentServicesPaymentEnabled &&
            this.featureCreditCardPaymentEnabled
              ? 'Applicable only for Payment and Card Services Transactions'
              : this.featurePaymentServicesPaymentEnabled
                ? 'Applicable only for Payment Services Transactions'
                : 'Applicable only for Card Services Transactions'
          ],
          icon: 'mdi-cash-sync',
          routeName: 'payment-adjustments'
        },
        {
          title: 'Payment Services',
          text: [
            'Past & In Progress Payment Services transaction files',
            'Expected Payout date'
          ],
          icon: 'mdi-cash-clock',
          routeName: 'payment-transactions',
          hidden:
            !this.featurePaymentServicesPaymentEnabled ||
            this.processesOrdersOffline
        },
        {
          title: 'Card Services',
          text: [
            'Past & In Progress Card Services transaction files',
            'Expected Payout date'
          ],
          icon: 'mdi-credit-card-clock-outline',
          routeName: 'card-transactions',
          hidden:
            !this.featureCreditCardPaymentEnabled ||
            this.processesOrdersOffline,
          disabled: !this.cardServicesAvailable,
          disabledReason:
            this.featureBankAccountsEnabled && !this.bankDetailsAvailable
              ? 'Complete bank details to unlock Card Services reports.'
              : null
        },
        {
          title: 'Transactions',
          text: [
            'Purchase Orders',
            'Order Queries',
            'Credit Returns',
            'Payment Adjustments'
          ].filter(
            (i) => i !== 'Credit Returns' || this.featureCreditReturnsEnabled
          ),
          icon: 'mdi-account-cash-outline',
          routeName: 'transactions'
        },
        {
          title: 'Fees Invoices',
          text: ['Payment Services Only', 'Trading Terms & Credit Return Fees'],
          icon: 'mdi-receipt-text-check',
          routeName: 'fees'
        }
      ]
    },

    salesReports() {
      return [
        {
          title: 'Forecast',
          text: [
            'Top customers, product demand, and turn-in signals',
            'Monthly sales and ordering trends'
          ],
          icon: 'mdi-chart-timeline-variant',
          routeName: 'forecast-report',
          hidden: !this.featureForecastEnabled
        },
        {
          title: 'Sales Metrics',
          text: ['Compare various metrics on different time periods'],
          icon: 'mdi-chart-box-outline',
          routeName: 'sales-metrics'
        },
        {
          title: 'Sales By Product(s)',
          text: [
            'Customise by Time Period',
            'Aggregation Type by Carton Qty or Revenue sold'
          ],
          icon: 'mdi-package-variant',
          routeName: 'sales-by-products'
        },
        {
          title: `Sales By ${this.isILR ? 'Customer' : 'Retailer'}(s)`,
          text: [
            'Customise by Time Period',
            'Aggregation Type by Carton Qty or Revenue sold'
          ],
          icon: 'mdi-store',
          routeName: 'sales-by-retailers'
        }
      ]
    }
  },

  methods: {
    openReport(card) {
      if (card.disabled) return
      this.$router.push({ name: card.routeName })
    }
  }
}
</script>

<style lang="scss" scoped>
#reports {
  .p-icon {
    width: 32px;
    height: 32px;
    margin-left: 8px;
    margin-right: 8px;
    color: $secondary-color;
  }

  .p-card-disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .p-disabled-text {
    color: #707070;
    font-size: 0.9rem;
  }
}

@media only screen and (min-width: 600px) {
  #reports {
    .p-card {
      min-height: 120px;
    }
  }
}

@media only screen and (min-width: 960px) {
  #reports {
    .mdi:before {
      font-size: 28px;
    }
  }
}
</style>
